import React, { useEffect, useState } from 'react';

import { Selectable } from '@vestahealthcare/common/enums/Enum';
import { translate } from '@vestahealthcare/common/i18n';
import {
  BaseEnum,
  CareTeamMemberType,
  Employee,
} from '@vestahealthcare/common/models';

import { FilterItem, FilterSelectable, Select, TextInput } from 'styleguide-v2';

import { GetCareTeamPersonsParams } from 'dash/src/services/CareTeamServices';
import { useDebounce } from 'dash/src/utils/debounceUtils';
import { getFilterItem } from 'dash/src/utils/filterUtils';

type KeyGetCareTeamParams = keyof GetCareTeamPersonsParams;

type Props = {
  data: {
    statuses: BaseEnum[];
    roles: CareTeamMemberType[];
    engagementOwners: Employee[];
  };
  filters?: {
    [x in KeyGetCareTeamParams]?: FilterItem;
  };
  loading?: boolean;
  onChange: (
    filters: {
      [x in KeyGetCareTeamParams]?: FilterItem;
    },
  ) => void;
};

export const CareTeamFiltersBar = ({
  data,
  filters,
  loading,
  onChange,
}: Props) => {
  const [name, setName] = useState<string>();

  const debouncedName = useDebounce(name, 300);

  useEffect(() => {
    setName((filters?.fullName?.value as FilterSelectable)?.value);
  }, [filters?.fullName]);

  useEffect(() => {
    if (debouncedName !== (filters?.fullName?.value as FilterSelectable)?.value)
      onChange({
        ...filters,
        fullName: getFilterItem(
          translate('careTeam.common.name'),
          debouncedName,
        ),
      });
  }, [debouncedName]);

  return (
    <>
      <Select
        allowAddNewValue
        className="grid-span-3"
        data-cy="care-team-filter-id"
        items={[]}
        multiple
        noOptionsText={translate('careTeam.typeToSearch')}
        onChange={(values: Selectable[]) =>
          onChange({
            ...filters,
            id: getFilterItem(
              translate('careTeam.common.id'),
              values.map(({ label }) => ({
                value: label,
                label,
              })),
            ),
          })
        }
        placeholder={translate('careTeam.common.id')}
        value={filters?.id?.value as FilterSelectable}
        size="xs"
      />
      <TextInput
        data-cy="care-team-filter-name"
        onChange={setName}
        placeholder={translate('careTeam.common.name')}
        size="xs"
        value={name}
      />
      <Select
        data-cy="care-team-filter-status"
        items={BaseEnum.toSelectable(data.statuses)}
        limitTags={1}
        loading={loading}
        multiple
        onChange={(values: Selectable[]) =>
          onChange({
            ...filters,
            statusId: getFilterItem(
              translate('careTeam.common.status'),
              values,
            ),
          })
        }
        placeholder={translate('careTeam.common.status')}
        placeholderV2
        size="xs"
        value={filters?.statusId?.value || []}
      />
      <Select
        data-cy="care-team-filter-role"
        items={CareTeamMemberType.toSelectable(data.roles)}
        limitTags={1}
        loading={loading}
        multiple
        onChange={(values: Selectable[]) =>
          onChange({
            ...filters,
            typeId: getFilterItem(translate('careTeam.common.role'), values),
          })
        }
        placeholder={translate('careTeam.common.role')}
        placeholderV2
        size="xs"
        value={filters?.typeId?.value || []}
      />
    </>
  );
};
